import { useState } from 'react';
import { Phone, MessageSquare } from 'lucide-react';
import { GOOGLE_BUSINESS } from '../../config/googleBusiness';
import { redirectToPhoneApp } from '../../shared/phoneAppRedirect';
import { useStoreHours } from '../../shared/useStoreHours';
import { nextOpenLabel } from '../../shared/businessHours';
import { CallbackRequest } from './CallbackRequest';

const conditionLabel = (c) => (c ? `${c.charAt(0).toUpperCase()}${c.slice(1)}` : '');

const buildSmsBody = (deviceQuery, offerPrice, condition) => {
  const device = (deviceQuery || '').trim();
  if (offerPrice > 0) return `Hi, I got a $${Math.round(offerPrice)} estimate for my ${device}${condition ? ` (${conditionLabel(condition)})` : ''}. Can you beat it?`;
  return device ? `Hi, I'd like a quote for my ${device}${condition ? ` (${conditionLabel(condition)} condition)` : ''}.` : "Hi, I'd like a quote for my device.";
};

const isMobileDevice = () => /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')
  || (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent || ''));

const SmsHelp = ({ idPrefix, body }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard?.writeText(body).then(() => setCopied(true)).catch(() => {});
  return (
    <div role="status" data-testid={`${idPrefix}-sms-help`} className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-slate-700">
      <p>Text us from your phone at <a href={`sms:+1${GOOGLE_BUSINESS.phoneRaw}`} className="font-bold text-emerald-800 underline" data-testid={`${idPrefix}-sms-help-number`}>{GOOGLE_BUSINESS.phone}</a></p>
      <p className="mt-1 italic text-slate-600">"{body}"</p>
      <button type="button" onClick={copy} data-testid={`${idPrefix}-sms-help-copy`} className="mt-1.5 font-semibold text-emerald-800 hover:underline">
        {copied ? 'Message copied' : 'Copy message'}
      </button>
    </div>
  );
};

// Call / Text / Callback bar for the Instant Estimate result + manual-quote screens.
export const EstimateContactBar = ({ idPrefix, heading, deviceQuery, offerPrice = 0, condition, className = '' }) => {
  const open = useStoreHours();
  const nextOpen = nextOpenLabel();
  const [blocked, setBlocked] = useState(false);
  const [smsHelp, setSmsHelp] = useState(false);
  const number = `+1${GOOGLE_BUSINESS.phoneRaw}`;
  const smsHref = `sms:${number}?&body=${encodeURIComponent(buildSmsBody(deviceQuery, offerPrice, condition))}`;
  const go = (e) => { e.preventDefault(); setBlocked(!redirectToPhoneApp(e.currentTarget.href)); };
  // Desktop browsers can't open sms: links, so show the number + message to send from a phone.
  const goText = (e) => {
    if (!isMobileDevice()) { e.preventDefault(); setSmsHelp(true); return; }
    go(e);
  };

  return (
    <section data-testid={`${idPrefix}-contact-bar`} className={`rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm ${className}`}>
      {heading && <p className="text-sm font-bold text-slate-900 mb-1" data-testid={`${idPrefix}-contact-heading`}>{heading}</p>}
      <p role="status" data-testid={`${idPrefix}-hours-status`} className="flex items-center gap-2 text-xs font-medium text-slate-600 mb-2.5">
        <span className={`h-2 w-2 rounded-full ${open ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} aria-hidden="true" />
        {open ? 'Open now, call us and we answer right away.' : `Closed now, text us and we'll reply ${nextOpen}.`}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <a href={`tel:${number}`} target="_top" onClick={go} data-testid={`${idPrefix}-call-btn`} data-openai-call-source={idPrefix}
          className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors ${open ? 'bg-blue-700 text-white hover:bg-blue-800' : 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50'}`}>
          <Phone className="h-4 w-4" /> Call
        </a>
        <a href={smsHref} target="_top" onClick={goText} data-testid={`${idPrefix}-text-btn`} data-openai-text-source={idPrefix}
          className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors ${open ? 'border border-emerald-700 bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : 'bg-emerald-700 text-white hover:bg-emerald-800'}`}>
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span className="flex flex-col items-start leading-tight"><span>Text</span>{!open && <span className="text-[10px] font-medium opacity-90 whitespace-nowrap">Best right now</span>}</span>
        </a>
      </div>
      {smsHelp && <SmsHelp idPrefix={idPrefix} body={buildSmsBody(deviceQuery, offerPrice, condition)} />}
      {blocked && <p role="alert" data-testid={`${idPrefix}-contact-error`} className="mt-2 text-xs text-red-700">This browser blocked the redirect. Please call {GOOGLE_BUSINESS.phone}.</p>}
      <div className="mt-2">
        <CallbackRequest idPrefix={idPrefix} open={open} nextOpen={nextOpen} deviceQuery={deviceQuery} offerPrice={offerPrice} condition={condition} />
      </div>
    </section>
  );
};
